import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_type: string;
  trade_type: string;
  subscription_period: string | null;
  amount: number;
  vat_amount: number;
  discount_amount: number;
  discount_code: string | null;
  total_amount: number;
  payment_status: string;
  payment_method: string;
  issued_date: string;
  provider_profiles: {
    users: {
      full_name: string;
      email: string;
    };
  };
}

interface SubscriptionInvoice {
  id: string;
  invoice_number: string;
  invoice_type: string;
  trade_category: string;
  provider_name: string;
  provider_email: string;
  subtotal: number;
  discount_amount: number;
  discount_code: string | null;
  vat_amount: number;
  total_amount: number;
  payment_method: string;
  stripe_payment_id: string | null;
  created_at: string;
}

export default function InvoicesManagement() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_invoices')
        .select(`
          *,
          provider_profiles(
            users(full_name, email)
          )
        `)
        .order('issued_date', { ascending: false });

      if (error) throw error;
      setInvoices(data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesType = filterType === 'all' || invoice.invoice_type === filterType;
    const matchesSearch = 
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.provider_profiles?.users?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.provider_profiles?.users?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  const totalRevenue = filteredInvoices.reduce((sum, inv) => sum + inv.total_amount, 0);
  const totalDiscounts = filteredInvoices.reduce((sum, inv) => sum + inv.discount_amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const downloadInvoice = (invoice: SubscriptionInvoice) => {
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 40px; border-bottom: 2px solid #14B8A6; padding-bottom: 20px; }
          .company { font-size: 24px; font-weight: bold; color: #14B8A6; }
          .company-details { font-size: 14px; color: #666; margin-top: 5px; }
          .invoice-details { text-align: right; }
          .invoice-number { font-size: 20px; font-weight: bold; }
          .section { margin: 30px 0; }
          .section-title { font-size: 16px; font-weight: bold; margin-bottom: 10px; color: #333; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background-color: #f8f9fa; font-weight: bold; }
          .total-row { font-weight: bold; font-size: 18px; background-color: #f8f9fa; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="company">Taskbudy Ltd</div>
            <div class="company-details">Company Number: 16087603</div>
            <div class="company-details">VAT Number: 497350946</div>
          </div>
          <div class="invoice-details">
            <div class="invoice-number">INVOICE</div>
            <div>${invoice.invoice_number}</div>
            <div>${new Date(invoice.created_at).toLocaleDateString('en-GB')}</div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Bill To:</div>
          <div>${invoice.provider_name}</div>
          <div>${invoice.provider_email}</div>
          <div>Trade: ${invoice.trade_category}</div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Period</th>
              <th style="text-align: right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${invoice.invoice_type === 'subscription' ? `${invoice.trade_category} Subscription` : 'Workwear Kit'}</td>
              <td>${invoice.invoice_type === 'subscription' ? 'Yearly' : 'One-time'}</td>
              <td style="text-align: right;">£${invoice.subtotal.toFixed(2)}</td>
            </tr>
            ${invoice.discount_amount > 0 ? `
            <tr>
              <td colspan="2">Discount${invoice.discount_code ? ` (${invoice.discount_code})` : ''}</td>
              <td style="text-align: right; color: #16a34a;">-£${invoice.discount_amount.toFixed(2)}</td>
            </tr>
            ` : ''}
            <tr>
              <td colspan="2">VAT (20%)</td>
              <td style="text-align: right;">£${invoice.vat_amount.toFixed(2)}</td>
            </tr>
            <tr class="total-row">
              <td colspan="2">Total Paid</td>
              <td style="text-align: right;">£${invoice.total_amount.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>

        <div class="section">
          <div class="section-title">Payment Details:</div>
          <div>Payment Method: ${invoice.payment_method}</div>
          ${invoice.stripe_payment_id ? `<div>Payment ID: ${invoice.stripe_payment_id}</div>` : ''}
          <div>Status: Paid</div>
        </div>

        <div class="footer">
          <p>Thank you for your business!</p>
          <p>Taskbudy Ltd - Professional Trade Services Platform</p>
          <p>Company Number: 16087603 | VAT Number: 497350946</p>
        </div>
      </body>
      </html>
    `;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Payment Invoices</h2>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm text-gray-600">Total Revenue</div>
            <div className="text-2xl font-bold text-teal-600">£{totalRevenue.toFixed(2)}</div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Total Discounts</div>
            <div className="text-2xl font-bold text-orange-600">£{totalDiscounts.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90">Total Invoices</p>
              <p className="text-2xl font-bold text-white">{invoices.length}</p>
            </div>
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg">
              <i className="ri-file-list-3-line text-2xl text-white"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90">Subscriptions</p>
              <p className="text-2xl font-bold text-white">
                {invoices.filter(i => i.invoice_type === 'subscription').length}
              </p>
            </div>
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg">
              <i className="ri-vip-crown-line text-2xl text-white"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90">Workwear</p>
              <p className="text-2xl font-bold text-white">
                {invoices.filter(i => i.invoice_type === 'workwear').length}
              </p>
            </div>
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg">
              <i className="ri-shirt-line text-2xl text-white"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90">This Month</p>
              <p className="text-2xl font-bold text-white">
                {invoices.filter(i => {
                  const date = new Date(i.issued_date);
                  const now = new Date();
                  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg">
              <i className="ri-calendar-line text-2xl text-white"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search by invoice number, provider name, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                filterType === 'all'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({invoices.length})
            </button>
            <button
              onClick={() => setFilterType('subscription')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                filterType === 'subscription'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Subscriptions ({invoices.filter(i => i.invoice_type === 'subscription').length})
            </button>
            <button
              onClick={() => setFilterType('workwear')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                filterType === 'workwear'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Workwear ({invoices.filter(i => i.invoice_type === 'workwear').length})
            </button>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      {filteredInvoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <i className="ri-file-list-3-line text-6xl text-gray-300 mb-4"></i>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Invoices Found</h3>
          <p className="text-gray-600">
            {searchTerm ? 'Try adjusting your search criteria' : 'Invoices will appear here once payments are made'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{invoice.invoice_number}</div>
                      {invoice.discount_code && (
                        <div className="text-xs text-green-600">
                          <i className="ri-coupon-line mr-1"></i>
                          {invoice.discount_code}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {invoice.provider_profiles?.users?.full_name || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {invoice.provider_profiles?.users?.email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          invoice.invoice_type === 'subscription'
                            ? 'bg-teal-100 text-teal-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {invoice.invoice_type === 'subscription' ? 'Subscription' : 'Workwear'}
                        </span>
                      </div>
                      {invoice.trade_type && (
                        <div className="text-xs text-gray-500 mt-1">{invoice.trade_type}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(invoice.issued_date).toLocaleDateString('en-GB')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(invoice.issued_date).toLocaleTimeString('en-GB', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        £{invoice.total_amount.toFixed(2)}
                      </div>
                      {invoice.discount_amount > 0 && (
                        <div className="text-xs text-green-600">
                          Saved £{invoice.discount_amount.toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {invoice.payment_method === 'stripe' ? 'Card' : 'Manual'}
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                        <i className="ri-checkbox-circle-fill mr-1"></i>
                        Paid
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setSelectedInvoice(invoice)}
                        className="text-teal-600 hover:text-teal-700 whitespace-nowrap"
                      >
                        <i className="ri-eye-line mr-1"></i>
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Invoice Details</h3>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Invoice Header */}
              <div className="text-center pb-6 border-b border-gray-200">
                <h2 className="text-3xl font-bold text-teal-600 mb-2">INVOICE</h2>
                <p className="text-lg text-gray-600">{selectedInvoice.invoice_number}</p>
              </div>

              {/* Provider Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Provider Information</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium text-gray-900">
                      {selectedInvoice.provider_profiles?.users?.full_name || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium text-gray-900">
                      {selectedInvoice.provider_profiles?.users?.email || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">Invoice Date:</span>
                  <span className="text-gray-900">
                    {new Date(selectedInvoice.issued_date).toLocaleDateString('en-GB')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">Payment Type:</span>
                  <span className="text-gray-900">
                    {selectedInvoice.invoice_type === 'subscription' ? 'Subscription Payment' : 'Workwear Purchase'}
                  </span>
                </div>
                {selectedInvoice.trade_type && (
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-700">Trade:</span>
                    <span className="text-gray-900">{selectedInvoice.trade_type}</span>
                  </div>
                )}
                {selectedInvoice.subscription_period && (
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-700">Period:</span>
                    <span className="text-gray-900">{selectedInvoice.subscription_period}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">Payment Method:</span>
                  <span className="text-gray-900">
                    {selectedInvoice.payment_method === 'stripe' ? 'Credit/Debit Card' : 'Manual Payment'}
                  </span>
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Payment Breakdown</h4>
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-700">
                      {selectedInvoice.invoice_type === 'subscription' ? 'Annual Subscription' : 'Workwear Kit'}
                    </span>
                    <span className="font-medium text-gray-900">£{selectedInvoice.amount.toFixed(2)}</span>
                  </div>
                  {selectedInvoice.discount_amount > 0 && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-green-600">
                        Discount ({selectedInvoice.discount_code})
                      </span>
                      <span className="font-medium text-green-600">
                        -£{selectedInvoice.discount_amount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-700">VAT (20%)</span>
                    <span className="font-medium text-gray-900">£{selectedInvoice.vat_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-3 bg-teal-50 px-4 rounded-lg">
                    <span className="text-lg font-bold text-gray-900">Total Paid</span>
                    <span className="text-lg font-bold text-teal-600">
                      £{selectedInvoice.total_amount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <div className="pt-4">
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="w-full px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
